/* Admin tab , reorder the 4 Ticketed Cruises on the home page.
   The first card in the order is the BIG featured card on the left,
   the remaining 3 stack on the right column. */
import React, { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Save, Loader2, Check } from "lucide-react";
import { adminApi } from "./adminApi";

const LABELS = {
 "vivid": { name: "Vivid Sydney Cruises", sub: "28 May → 19 Jun 2027 · $49 adult", color: "bg-fuchsia-50 ring-fuchsia-200 text-fuchsia-900" },
 "nye": { name: "NYE Fireworks Cruise", sub: "31 Dec 2026 · $1,250 pp", color: "bg-amber-50 ring-amber-200 text-amber-900" },
 "australia-day": { name: "Australia Day Cruise", sub: "26 Jan 2027 · $145/$70", color: "bg-cyan-50 ring-cyan-200 text-cyan-900" },
 "boxing-day": { name: "Boxing Day Cruise", sub: "26 Dec 2026 · Sydney to Hobart · $110/$45", color: "bg-emerald-50 ring-emerald-200 text-emerald-900" },
};

export default function AdminHomeOrder() {
 const [order, setOrder] = useState(null);
 const [original, setOriginal] = useState(null);
 const [saving, setSaving] = useState(false);
 const [savedAt, setSavedAt] = useState(null);
 const [error, setError] = useState("");

 useEffect(() => {
  adminApi("/cms/home-order").then(d => {
   setOrder(d.order);
   setOriginal(d.order);
  }).catch(e => setError(e.message));
 }, []);

 const move = (idx, dir) => {
  const next = idx + dir;
  if (next < 0 || next >= order.length) return;
  const newOrder = [...order];
  [newOrder[idx], newOrder[next]] = [newOrder[next], newOrder[idx]];
  setOrder(newOrder);
  setSavedAt(null);
 };

 const reset = () => {
  setOrder(original);
  setSavedAt(null);
 };

 const save = async () => {
  setSaving(true);
  setError("");
  try {
   const res = await adminApi("/admin/cms/home-order", { method: "PUT", body: { order } });
   setOriginal(res.order);
   setSavedAt(Date.now());
  } catch (e) {
   setError(e.message);
  } finally {
   setSaving(false);
  }
 };

 if (!order) return <div className="text-slate-500 text-sm">Loading…</div>;

 const dirty = JSON.stringify(order) !== JSON.stringify(original);

 return (
  <div className="space-y-6" data-testid="admin-home-order">
   <div>
    <h2 className="text-2xl font-black text-slate-900 tracking-tight">Home page · Ticketed Cruises order</h2>
    <p className="text-slate-600 text-sm mt-1">The first card becomes the BIG featured card on the left. The other three stack on the right column.</p>
   </div>

   <ol className="space-y-2.5" data-testid="home-order-list">
    {order.map((id, idx) => {
     const meta = LABELS[id] || { name: id, sub: "", color: "bg-slate-50 ring-slate-200 text-slate-900" };
     const isFeatured = idx === 0;
     return (
      <li key={id} className={`flex items-center gap-3 rounded-2xl ring-1 px-4 py-3.5 ${meta.color} ${isFeatured ? "shadow-md" : ""}`} data-testid={`home-order-row-${id}`}>
       <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black ${isFeatured ? "bg-slate-900 text-white" : "bg-white ring-1 ring-slate-200 text-slate-700"}`}>
        {idx + 1}
       </div>
       <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
         <div className="font-black text-sm tracking-tight">{meta.name}</div>
         {isFeatured && <span className="text-[9px] uppercase tracking-[0.22em] font-black bg-slate-900 text-white px-2 py-0.5 rounded-full">Big · Featured</span>}
        </div>
        <div className="text-xs opacity-70 mt-0.5">{meta.sub}</div>
       </div>
       <div className="flex items-center gap-1.5 shrink-0">
        <button onClick={() => move(idx, -1)} disabled={idx === 0} className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-300 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors" data-testid={`home-order-up-${id}`} aria-label="Move up">
         <ArrowUp className="w-4 h-4" />
        </button>
        <button onClick={() => move(idx, 1)} disabled={idx === order.length - 1} className="w-8 h-8 rounded-lg bg-white ring-1 ring-slate-300 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors" data-testid={`home-order-down-${id}`} aria-label="Move down">
         <ArrowDown className="w-4 h-4" />
        </button>
       </div>
      </li>
     );
    })}
   </ol>

   <div className="flex items-center gap-3 pt-2">
    <button onClick={save} disabled={!dirty || saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-testid="home-order-save">
     {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
     {saving ? "Saving…" : "Save order"}
    </button>
    {dirty && !saving && (
     <button onClick={reset} className="text-sm text-slate-500 hover:text-slate-700 underline-offset-2 hover:underline" data-testid="home-order-reset">
      Discard changes
     </button>
    )}
    {savedAt && !dirty && (
     <div className="inline-flex items-center gap-1.5 text-emerald-600 text-sm font-medium" data-testid="home-order-saved">
      <Check className="w-4 h-4" /> Saved · live on home page
     </div>
    )}
    {error && <div className="text-rose-600 text-sm font-medium" data-testid="home-order-error">{error}</div>}
   </div>
  </div>
 );
}
